import React, { useState } from 'react';
import { Eye, RefreshCw } from 'lucide-react';
import { MARKETPLACE_LOGOS, getSupplierLogo, API_BASE } from '../consts';
import { calculateReferralFee, calculateWFSFee, detectPackCount } from '../utils';
import DealCard from './DealCard';
import authService from '../services/AuthService';
import FavoriteButton from './FavoriteButton';
import { CopyButton, PackSizeButton, FeeSettings, KeepaSettingsModal, MultiSourcePopup, CashbackModal } from './DealComponents';
import UniversalFavoriteButton from './UniversalFavoriteButton';
import FindBundlesButton from './FindBundlesButton';

const MinimizedDealCard = ({ deal, alternativeSources = [], couponAmount = 0, fulfillmentType = null, onFulfillmentChange = null, onRefreshGated, refreshing, onSettingsChange, onHideDeal, amazonLinked = false, autoPackDetection = true }) => {
    const [showFullCard, setShowFullCard] = useState(false);
    const [feeSettings, setFeeSettings] = useState({});
    const [showKeepaSettings, setShowKeepaSettings] = useState(false);
    const [keepaChartCache, setKeepaChartCache] = useState({});
    const [keepaChartLoading, setKeepaChartLoading] = useState(false);
    const [triggeringReverseScan, setTriggeringReverseScan] = useState(false);
    const [showMultiSourcePopup, setShowMultiSourcePopup] = useState(false);
    const [showFeeSettings, setShowFeeSettings] = useState(false);
    const [showWfsDimsModal, setShowWfsDimsModal] = useState(false);
    const initialPack = (() => {
        try {
            const pack = deal.marketplace_data?.pack;
            if (pack && pack > 1) {
                const sp = deal.source_price || 0;
                const azPrice = deal.marketplace_data?.az_price || 0;
                const fbaFee = deal.marketplace_data?.fba_fee || 0;
                const refFee = azPrice * (deal.marketplace_data?.ref_fee_perc || 0.15);
                const roi = sp > 0 ? ((azPrice - sp - fbaFee - refFee) / sp) * 100 : 0;
                if (roi > 100) return pack;
            }
            // Backend pack not usable — fall back to title detection
            if (!autoPackDetection) return 1;
            const titleToCheck = deal.marketplace === 'amazon'
                ? deal.marketplace_data?.title || deal.title || ''
                : deal.marketplace === 'walmart'
                    ? deal.marketplace_data?.name || deal.marketplace_data?.title || deal.title || ''
                    : deal.title || '';
            return detectPackCount(titleToCheck);
        } catch {
            return 1;
        }
    })();
    const [packSize, setPackSize] = useState(initialPack);
    const [showCashbackModal, setShowCashbackModal] = useState(false);
    const [showPopupBlockedModal, setShowPopupBlockedModal] = useState(false);
    const [showSearchingPopup, setShowSearchingPopup] = useState(false);
    const [manualCostInput, setManualCostInput] = useState(() => {
        const overrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');
        return overrides[deal.id]?.source_price ?? '';
    });
    const manualCostSaveTimer = React.useRef(null);

    // Load price overrides from localStorage
    const priceOverrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');
    const dealOverride = priceOverrides[deal.id] || {};

    // Apply overrides to deal object
    if (dealOverride.source_price !== undefined) {
        deal.source_price = dealOverride.source_price;
    }
    // Local manual cost (for no-supplier state) overrides everything, live, no re-render round trip needed
    if (manualCostInput !== '' && !isNaN(parseFloat(manualCostInput))) {
        deal.source_price = parseFloat(manualCostInput);
    }
    if (dealOverride.sell_price !== undefined) {
        const data = deal.marketplace_data || {};
        if (deal.marketplace === 'amazon') {
            data.az_price = dealOverride.sell_price;
        } else if (deal.marketplace === 'walmart') {
            data.price_value = dealOverride.sell_price;
        } else if (deal.marketplace === 'ebay') {
            data.lowest_price = dealOverride.sell_price;
        }
    }

    const hasMultipleSources = alternativeSources.length > 0;
    const allSources = [deal, ...alternativeSources];
    const totalSources = allSources.length;
    const openUrl = deal.product_url || (deal.source_link && /^https?:\/\//i.test(deal.source_link) ? deal.source_link : null);

    const supplierLogo = getSupplierLogo(deal.supplier_name);
    const marketplaceLogo = MARKETPLACE_LOGOS[deal.marketplace];

    React.useEffect(() => {
        setPackSize(initialPack);
    }, [autoPackDetection]);

    React.useEffect(() => {
        const storageKey = `${deal.marketplace}_fee_settings`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            setFeeSettings(JSON.parse(saved));
        }
    }, [deal.marketplace]);

    const handleRevealClick = (e) => {
        e.preventDefault();
        const upc = deal.upc;
        const hasValidUpc = upc && String(upc).trim().replace(/\s/g, '').match(/^\d+$/);

        if (!hasValidUpc) {
            if (!localStorage.getItem('no_upc_alert_shown')) {
                alert('ℹ️ Important: When we don\'t have enough data to run our algorithm (such as a valid UPC), we will automatically open a manual Google search for you instead.\n\nThis message will not be shown again.');
                localStorage.setItem('no_upc_alert_shown', 'true');
            }
            const title = deal.title || deal.marketplace_data?.title || deal.marketplace_data?.name || '';
            if (title) {
                const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                const EXTidentifier = deal.marketplace === 'amazon' ? (
                    deal?.marketplace_data?.asin
                ) : (
                    deal.marketplace === 'walmart' ? (
                        deal?.marketplace_data?.item_id
                    ) : (
                        deal.marketplace === 'ebay' ? (
                            deal?.marketplace_data?.upc
                        ) : ''
                    )
                );
                if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                    window.chrome.runtime.sendMessage(EXT_ID, {
                        type: 'MSP_SEARCH_INTENT',
                        marketplace: deal.marketplace,
                        identifier: EXTidentifier,
                        title: deal.title,
                        deal,
                    });
                }
                window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
            }
            return;
        }

        // Instead of triggering single deal, fire the global reveal all flow
        if (window.__msp_openSupplierPicker) {
            window.__msp_openSupplierPicker();
        }
    };

    const saveManualCost = (val) => {
        if (manualCostSaveTimer.current) clearTimeout(manualCostSaveTimer.current);
        manualCostSaveTimer.current = setTimeout(() => {
            const overrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');
            if (!overrides[deal.id]) overrides[deal.id] = {};
            overrides[deal.id].source_price = val;
            overrides[deal.id].manual_source_price = true;
            localStorage.setItem('deal_price_overrides', JSON.stringify(overrides));
            if (onSettingsChange) onSettingsChange();
        }, 4000);
    };

    const handleManualCostEdit = (e) => {
        e.stopPropagation();
        const overrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');
        const currentOverride = overrides[deal.id]?.source_price;
        const promptText = currentOverride !== undefined
            ? `Edit manual cost price (currently $${currentOverride.toFixed(2)}):`
            : 'No supplier found yet — enter a cost price to calculate ROI:';
        const newPrice = prompt(promptText, currentOverride !== undefined ? currentOverride.toFixed(2) : '');
        if (newPrice !== null && !isNaN(parseFloat(newPrice))) {
            if (!overrides[deal.id]) overrides[deal.id] = {};
            overrides[deal.id].source_price = parseFloat(newPrice);
            overrides[deal.id].manual_source_price = true;
            localStorage.setItem('deal_price_overrides', JSON.stringify(overrides));
            if (onSettingsChange) onSettingsChange();
        }
    };

    const handleTriggerReverseScan = async (dealId) => {
        if (triggeringReverseScan) return;

        setTriggeringReverseScan(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + (window.location.pathname.includes('reverse-scan') ? '/api/reverse-scan/deals/trigger' : '/api/storefront/reverse-scan/trigger'),
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ deal_ids: [dealId] })
                }
            );

            if (response.ok) {
                deal.reverse_scan_status = 'pending';
                if (onSettingsChange) {
                    onSettingsChange();
                }
            } else {
                const data = await response.json();
                alert(data.error || 'Failed to trigger reverse scan');
            }
        } catch (error) {
            console.error('Error triggering reverse scan:', error);
            alert('Failed to trigger reverse scan');
        } finally {
            setTriggeringReverseScan(false);
        }
    };

    const fetchKeepaChart = async () => {
        if (deal.marketplace !== 'amazon' || !data.asin) return;

        const savedSettings = JSON.parse(localStorage.getItem('keepa_chart_settings') || '{"range":90,"salesrank":true,"amazon":true,"new":true,"bb":true,"fba":true}');
        const cacheKey = `${data.asin}_${savedSettings.range}_${savedSettings.salesrank}_${savedSettings.amazon}_${savedSettings.new}_${savedSettings.bb}_${savedSettings.fba}`;

        if (keepaChartCache[cacheKey]) {
            return;
        }

        setKeepaChartLoading(true);
        try {
            const params = new URLSearchParams({
                domain: { '.com': '1', '.co.uk': '2', '.ca': '6', '.com.mx': '11' }[amazonDomain] || '1',
                salesrank: savedSettings.salesrank ? '1' : '0',
                amazon: savedSettings.amazon ? '1' : '0',
                new: savedSettings.new ? '1' : '0',
                bb: savedSettings.bb ? '1' : '0',
                fba: savedSettings.fba ? '1' : '0',
                range: savedSettings.range.toString()
            });

            const url = `${API_BASE}/api/keepa/chart/${data.asin}?${params.toString()}`;
            const response = await authService.authenticatedFetch(url);
            const responseData = await response.json();

            if (responseData && responseData.url) {
                setKeepaChartCache(prev => ({ ...prev, [cacheKey]: responseData.url }));
            } else {
                setKeepaChartCache(prev => ({ ...prev, [cacheKey]: 'ERROR' }));
            }
        } catch (error) {
            setKeepaChartCache(prev => ({ ...prev, [cacheKey]: 'ERROR' }));
        } finally {
            setKeepaChartLoading(false);
        }
    };

    const cleanTitle = (title) => {
        if (!title) return '';
        return title.replace(/\(Seller:\s*[^)]+\)\s*/gi, '').trim();
    };

    const getSellerNote = () => {
        const titleLower = (deal.title || '').toLowerCase();
        const isWalmartSupplier = deal.supplier_name?.toLowerCase() === 'walmart';
        const isAmazonSupplier = deal.supplier_name?.toLowerCase() === 'amazon';

        const hasWalmartSeller = titleLower.includes('(seller: walmart.com)') || titleLower.includes('(seller: walmart)');

        const isA2aDomain = window.location.href.includes('a2a-deals');
        const marketplaceTitleLower = isA2aDomain ? (deal.marketplace_data?.title || '').toLowerCase() : '';

        const hasAmazonSeller =
            titleLower.includes('(seller: amazon.com)') ||
            titleLower.includes('(seller: amazon)') ||
            (isA2aDomain && (
                marketplaceTitleLower.includes('(seller: amazon.com)') ||
                marketplaceTitleLower.includes('(seller: amazon)')
            ));

        if (isWalmartSupplier && titleLower.includes('(seller:')) {
            if (hasWalmartSeller) {
                return { type: 'walmart', note: '✓ Walmart is the seller', isGood: true };
            } else {
                return { type: 'walmart', note: '⚠️ Walmart is NOT the seller', isGood: false };
            }
        }

        if (isAmazonSupplier) {
            if (hasAmazonSeller) {
                return { type: 'amazon', note: '✓ Amazon is the seller', isGood: true };
            } else {
                return { type: 'amazon', note: '⚠️ Amazon is NOT the seller', isGood: false };
            }
        }

        return null;
    };

    const getGatedBadge = (status, asin) => {
        const handleClick = () => {
            if (asin && (status === 'gated' || status === 'approval' || status === 'checking')) {
                window.open(
                    `https://sellercentral.amazon${amazonDomain}/hz/approvalrequest/restrictions/approve?asin=${asin}`,
                    "_blank"
                );
            }
        };

        switch (status) {
            case 'ungated':
                return (
                    <span
                        className="text-green-900 bg-green-100 px-2 py-1 rounded text-xs font-bold cursor-pointer hover:bg-green-200 transition border border-green-400"
                        onClick={() => asin && window.open(`https://sellercentral.amazon${amazonDomain}/product-search/search?q=${asin}`, "_blank")}
                        title="Click to view on Seller Central"
                    >
                        ✓ Ungated
                    </span>
                );
            case 'approval':
                return (
                    <span
                        className="text-orange-900 bg-orange-100 px-2 py-1 rounded text-xs font-bold cursor-pointer hover:bg-orange-200 transition border border-orange-400"
                        onClick={handleClick}
                        title="Click to apply for approval on Seller Central"
                    >
                        ⚠ Approval
                    </span>
                );
            case 'gated':
                return (
                    <span
                        className="text-red-900 bg-red-100 px-2 py-1 rounded text-xs font-bold cursor-pointer hover:bg-red-200 transition border border-red-500"
                        onClick={handleClick}
                        title="Click to check restrictions on Seller Central"
                    >
                        ✗ Gated
                    </span>
                );
            case 'checking':
            case 'error':
                return (
                    <span className="text-blue-900 bg-blue-100 px-2 py-1 rounded text-xs font-bold cursor-pointer hover:bg-blue-200 transition border border-blue-400"
                        onClick={handleClick}
                        title="Click to check restrictions on Seller Central"
                    >
                        Checking...
                    </span>
                );
            default:
                return false;
        }
    };

    const calculateMetrics = () => {
        const sourcePrice = (deal.source_price || 0) * (1 - (couponAmount / 100)) * packSize;
        const data = deal.marketplace_data || {};

        const itemWeight = data.weight || deal.weight || 1;
        const inboundShippingPerLb = feeSettings.inboundShipping || 0;
        const inboundShippingCost = inboundShippingPerLb * itemWeight;

        if (deal.marketplace === 'amazon') {
            const azPrice = data.az_price || 0;
            const fbaFee = fulfillmentType === 'fba' ? (data.fba_fee || 0) : 0;
            const refFeePerc = data.ref_fee_perc || 0.15;
            const refFee = azPrice * refFeePerc;
            const placementFees = fulfillmentType === 'fba' ? (feeSettings.placementFees || 0) : 0;
            const inboundShipping = fulfillmentType === 'fba' ? inboundShippingCost : 0;
            const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            const otherCosts = feeSettings.otherCosts || 0;
            const fulfillmentCost = fulfillmentType !== 'fba' ? (feeSettings.fulfillmentCost || 0) : 0;

            const totalFees = fbaFee + refFee + placementFees + inboundShipping + salesTaxAmount + otherCosts + fulfillmentCost;
            const profit = azPrice - sourcePrice - totalFees;
            const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

            return {
                sellPrice: azPrice,
                totalFees,
                profit,
                roi,
                breakdown: { fbaFee, refFee, refFeePerc: refFeePerc * 100, placementFees, inboundShipping, salesTax: salesTaxAmount, otherCosts, fulfillmentCost }
            };
        } else if (deal.marketplace === 'walmart') {
            const wmPrice = data.price_value || 0;

            let wfsFee = 0;
            if (fulfillmentType === 'wfs') {
                if (data.dimensions && itemWeight) {
                    wfsFee = calculateWFSFee(
                        data.dimensions,
                        itemWeight,
                        {
                            isApparel: data.category?.toLowerCase().includes("clothing") ||
                                data.category?.toLowerCase().includes("apparel") ||
                                data.sub_category?.toLowerCase().includes("clothing") ||
                                data.sub_category?.toLowerCase().includes("apparel"),
                            retailPrice: wmPrice
                        }
                    );
                } else {
                    wfsFee = 3.45;
                }
            }

            const refFee = calculateReferralFee(wmPrice, data.category, data.sub_category);
            const refFeePerc = wmPrice > 0 ? (refFee / wmPrice) * 100 : 15;
            const inboundShipping = fulfillmentType === 'wfs' ? inboundShippingCost : 0;
            const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            const otherCosts = feeSettings.otherCosts || 0;
            const fulfillmentCost = fulfillmentType !== 'wfs' ? (feeSettings.fulfillmentCost || 0) : 0;

            const totalFees = wfsFee + refFee + inboundShipping + salesTaxAmount + otherCosts + fulfillmentCost;
            const profit = wmPrice - sourcePrice - totalFees;
            const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

            return {
                sellPrice: wmPrice,
                totalFees,
                profit,
                roi,
                breakdown: {
                    wfsFee,
                    refFee,
                    refFeePerc,
                    inboundShipping,
                    salesTax: salesTaxAmount,
                    otherCosts,
                    fulfillmentCost
                },
                missingData: {
                    weight: !itemWeight,
                    dimensions: !data.dimensions || !data.dimensions.length || !data.dimensions.width || !data.dimensions.height,
                    category: !data.category
                }
            };
        } else if (deal.marketplace === 'ebay') {
            const ebayPrice = data.lowest_price || 0;
            const shippingCharge = 0;

            const totalSale = ebayPrice + shippingCharge;

            const categoryRates = {
                'most': 0.136,
                'books': 0.153,
                'movies': 0.153,
                'music': 0.153,
                'coins': 0.1325,
                'bullion': totalSale <= 7500 ? 0.136 : 0.07,
                'handbags': totalSale <= 2000 ? 0.15 : 0.09,
                'collectibles': 0.1325,
                'jewelry': totalSale <= 5000 ? 0.15 : 0.09,
                'watches': 0.15,
                'guitars': 0.067,
                'athletic_shoes': totalSale >= 150 ? 0.08 : 0.136,
                'nfts': 0.05
            };

            const categoryFromData = (data.category || 'most').toLowerCase();
            let category = 'most';
            if (categoryRates[categoryFromData]) {
                category = categoryFromData;
            }

            let baseFeeRate = categoryRates[category] || 0.136;

            const storeSubscription = feeSettings.storeSubscription || 'none';
            const isTopRated = feeSettings.topRated || false;

            const storeDiscounts = {
                'none': 0,
                'basic': 0.066,
                'premium': 0.088,
                'anchor': 0.102,
                'enterprise': 0.110
            };

            const storeDiscount = storeDiscounts[storeSubscription] || 0;
            const topRatedDiscount = isTopRated ? 0.10 : 0;
            const totalDiscount = storeDiscount + topRatedDiscount;

            let finalValueFee = 0;

            if (category === 'watches') {
                if (totalSale <= 1000) {
                    finalValueFee = totalSale * 0.15;
                } else if (totalSale <= 7500) {
                    finalValueFee = (1000 * 0.15) + ((totalSale - 1000) * 0.065);
                } else {
                    finalValueFee = (1000 * 0.15) + (6500 * 0.065) + ((totalSale - 7500) * 0.03);
                }
                finalValueFee = finalValueFee * (1 - totalDiscount);
            } else if (category === 'bullion' && totalSale > 7500) {
                finalValueFee = totalSale * 0.07;
                finalValueFee = finalValueFee * (1 - totalDiscount);
            } else {
                if (totalSale <= 7500) {
                    finalValueFee = totalSale * baseFeeRate;
                } else {
                    finalValueFee = (7500 * baseFeeRate) + ((totalSale - 7500) * 0.0235);
                }
                finalValueFee = finalValueFee * (1 - totalDiscount);
            }

            let perOrderFee = 0;
            if (category === 'athletic_shoes' && totalSale >= 150) {
                perOrderFee = 0;
            } else {
                perOrderFee = totalSale <= 10 ? 0.30 : 0.40;
            }

            const paymentProcessingFee = (totalSale * 0.0235) + 0.30;

            const shippingCost = 0;

            const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            const packagingCost = feeSettings.packagingCost || 0;
            const otherCosts = feeSettings.otherCosts || 0;

            const totalFees = finalValueFee + perOrderFee + paymentProcessingFee + shippingCost + salesTaxAmount + packagingCost + otherCosts;
            const profit = ebayPrice + shippingCharge - sourcePrice - totalFees;
            const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

            return {
                sellPrice: ebayPrice,
                totalFees,
                profit,
                roi,
                breakdown: {
                    finalValueFee,
                    perOrderFee,
                    paymentProcessingFee,
                    shippingCharge,
                    shippingCost,
                    salesTax: salesTaxAmount,
                    packagingCost,
                    otherCosts,
                    category
                }
            };
        }

        return { sellPrice: 0, totalFees: 0, profit: 0, roi: 0, breakdown: {} };
    };

    const hasManualCost = manualCostInput !== '' && !isNaN(parseFloat(manualCostInput));

    const calcProfit = () => {
        if (!deal.supplier_name && !hasManualCost) {
            return 0;
        } else {
            return metrics.profit;
        }
    };

    const calcCostPriceColor = () => {
        switch (deal.reverse_scan_status) {
            case 'failed':
                return "bg-red-50 border border-red-200";
            case 'no_match':
                return "bg-orange-50 border border-orange-200";
            case 'not_scanned':
                return "bg-gray-100 border-gray-300"
            default:
                if (supplierLogo === "checking.gif") {
                    return "bg-blue-50 border border-blue-200";
                }
                return "bg-yellow-50 border border-yellow-200"
        }
    };

    const amazonDomain = authService.getAmazonMarketplace();
    const currency = authService.getAmazonCurrency();
    const metrics = calculateMetrics();
    const data = deal.marketplace_data || {};
    const cleanedTitle = cleanTitle(deal.title);
    const sellerNote = getSellerNote();
    const profit = calcProfit();
    const costPriceColor = calcCostPriceColor();

    const getQuickLinks = () => {
        const links = [];

        const marketplaceUrl =
            deal.marketplace === 'amazon' && data.asin
                ? `https://www.amazon${amazonDomain}/dp/${data.asin}`
                : deal.marketplace === 'walmart' && data.item_id
                    ? `https://www.walmart.com/ip/${data.item_id}`
                    : deal.marketplace === 'ebay' && data.ebay_search_link
                        ? data.ebay_search_link
                        : null;

        if (openUrl && marketplaceUrl) {
            links.push({
                label: 'Open Both',
                action: () => {
                    if (openUrl) {
                        window.open(openUrl, '_blank');
                        const secondWindow = window.open(marketplaceUrl, '_blank');
                        if (!secondWindow || secondWindow.closed || typeof secondWindow.closed === 'undefined') {
                            setShowPopupBlockedModal(true);
                        }
                    }
                },
                icon: '🔗'
            });
        }

        if (deal.marketplace === 'amazon' && data.asin) {
            links.push({
                label: 'Keepa',
                url: `https://keepa.com/#!product/${{ '.com': '1', '.co.uk': '2', '.ca': '6', '.com.mx': '8' }[amazonDomain] || '1'}-${data.asin}`,
                icon: '📊',
                showChart: true
            });
        }

        return links;
    };

    const quickLinks = getQuickLinks();

    return (
        <>
            {deal.is_new && (
                <div className="absolute -mt-3 z-10">
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse shadow-lg">
                        NEW
                    </span>
                </div>
            )}

            {showKeepaSettings && (
                <KeepaSettingsModal
                    onClose={() => setShowKeepaSettings(false)}
                    onSave={(newSettings) => {
                        localStorage.setItem('keepa_chart_settings', JSON.stringify(newSettings));
                        setKeepaChartCache({});
                    }}
                />
            )}

            <div className="border border-gray-200 rounded-lg hover:shadow-md transition bg-white p-3">
                {/* Title, Seller Note, ASIN/UPC, Links */}
                <div className="flex flex-wrap items-center justify-between gap-2 md:gap-4 mb-2">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            {deal.is_universal ? (
                                <UniversalFavoriteButton deal={deal} onFavoriteChange={() => { }} />
                            ) : (
                                <FavoriteButton deal={deal} onSettingsChange={onSettingsChange} />
                            )}

                            {sellerNote && (
                                <div className="relative group flex-shrink-0">
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center cursor-help ${sellerNote.isGood
                                        ? (sellerNote.type === 'walmart' ? 'bg-blue-100' : 'bg-green-100')
                                        : (sellerNote.type === 'walmart' ? 'bg-orange-100' : 'bg-red-100')
                                        }`}>
                                        <img
                                            src={sellerNote.type === 'walmart' ? MARKETPLACE_LOGOS.walmart : MARKETPLACE_LOGOS.amazon}
                                            alt={sellerNote.type}
                                            className="w-3 h-3 object-contain"
                                        />
                                    </div>
                                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        {sellerNote.note}
                                        <div className="absolute top-full left-4 -mt-1">
                                            <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-gray-800"></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {deal.found_in_stores_count > 1 && (
                                <span className="relative group/multistore inline-flex items-center gap-1 bg-blue-100 text-blue-800 border border-blue-300 px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 cursor-help">
                                    🏪 {deal.found_in_stores_count}
                                    {deal.found_in_stores?.length > 0 && (
                                        <div className="absolute left-0 top-full mt-1 hidden group-hover/multistore:block z-50 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                            Found in: {deal.found_in_stores.join(', ')}
                                        </div>
                                    )}
                                </span>
                            )}
                            <h3 className="font-semibold text-sm text-gray-800 truncate">{cleanedTitle}</h3>

                            {/* Hide Deal Button */}
                            {onHideDeal && (
                                <button
                                    onClick={(e) => {
                                        onHideDeal(deal.id, false);
                                    }}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        onHideDeal(deal.id, true);
                                    }}
                                    className="text-red-600 hover:text-red-800 cursor-pointer flex-shrink-0"
                                    title="Click to remove (with confirmation), Right-click to remove immediately"
                                >
                                    <span className="text-sm">🗑️</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs text-gray-500">
                        {/* Find Bundles — wholesale only */}
                        {window.location.pathname.includes('wholesale') && deal.marketplace !== 'ebay' && (
                            <FindBundlesButton deal={deal} />
                        )}

                        {deal.marketplace === 'amazon' && data.asin && (
                            <div className="flex items-center gap-1">
                                <span className="font-semibold">ASIN:</span>
                                <span>{data.asin}</span>
                                <CopyButton text={data.asin} />
                            </div>
                        )}

                        {deal.marketplace === 'walmart' && data.item_id && (
                            <div className="flex items-center gap-1">
                                <span className="font-semibold">Item ID:</span>
                                <span>{data.item_id}</span>
                                <CopyButton text={data.item_id} />
                            </div>
                        )}

                        {(
                            <div className={`flex items-center gap-1${(!deal.upc || deal.upc === "N/A") ? " text-red-700" : ""}`}>
                                <span className="font-semibold">UPC:</span>
                                <span>{deal.upc || "N/A"}</span>
                                <CopyButton text={deal.upc} />
                            </div>
                        )}

                        {deal.source_link && (
                            /^https?:\/\//i.test(deal.source_link) ? (
                                <a
                                    href={deal.source_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-teal-600 hover:text-teal-800 hover:underline font-semibold"
                                >
                                    <span>🔗</span> Source Link
                                </a>
                            ) : (
                                <span
                                    className="flex items-center gap-1 text-gray-600 hover:text-gray-800 font-semibold"
                                >
                                    <span>🏷️</span> SKU: {deal.source_link} <CopyButton text={deal.source_link} />
                                </span>
                            )
                        )}

                        {/* Date Warning */}
                        {(() => {
                            const parseDate = (d) => { try { const dt = new Date(d.replace(' ', 'T') + (/(Z|[+-]\d{2}:\d{2})$/.test(d) ? '' : 'Z')); return isNaN(dt) ? new Date(d) : dt; } catch { return new Date(d); } };
                            const formatDays = (date) => { const d = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24)); return d === 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d}d ago`; };
                            const isStorefront = window.location.pathname.includes('storefront-scan');

                            if (isStorefront) {
                                return (
                                    <div className="flex items-center gap-1 text-gray-500">
                                        <span className="font-semibold">First Seen:</span>
                                        <span>{formatDays(parseDate(deal.created_at))}</span>
                                    </div>
                                );
                            }

                            const dealDate = parseDate(deal.updated_at || deal.created_at);
                            const daysDiff = Math.floor((new Date() - dealDate) / (1000 * 60 * 60 * 24));
                            const isOld = daysDiff >= 3;
                            return (
                                <div className={`flex items-center gap-1 ${isOld ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>
                                    {isOld && <span>⚠️</span>}
                                    <span className="font-semibold">{deal.updated_at ? 'Updated:' : 'Created:'}</span>
                                    <span className="text-xs">{daysDiff === 0 ? 'Today' : daysDiff === 1 ? 'Yesterday' : `${daysDiff}d ago`}</span>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* Main Row */}
                <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 md:gap-4 items-center">
                    {/* Images Section */}
                    <div className="flex gap-3 pt-2 relative">
                        {/* Buy From Image */}
                        <div className="flex flex-col items-center gap-1">
                            {deal.reverse_scan_status === "not_scanned" ? (
                                <button
                                    onClick={handleRevealClick}
                                    className="relative group cursor-pointer"
                                    disabled={triggeringReverseScan}
                                    title={deal.upc && String(deal.upc).trim().replace(/\s/g, '').match(/^\d+$/) ? "Click to find supplier" : "No UPC - click for Universal Reverse Scan"}
                                >
                                    <img
                                        src={deal.upc && String(deal.upc).trim().replace(/\s/g, '').match(/^\d+$/) ? (deal.img || '/reveal.png') : '/noupc.png'}
                                        alt="Source"
                                        className="w-16 h-16 object-contain rounded border-2 border-yellow-400 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-yellow-400 relative bg-white"
                                        onError={(e) => e.target.src = '/reveal.png'}
                                    />
                                </button>
                            ) : deal.reverse_scan_status === "failed" ? (
                                <div className="relative group">
                                    <img
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const upc = deal.marketplace_data?.upc || deal.marketplace_data?.ean || deal.marketplace_data?.gtin || deal.upc || '';
                                            const title = deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                            const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                            const EXTidentifier = deal.marketplace === 'amazon' ? (
                                                deal?.marketplace_data?.asin
                                            ) : (
                                                deal.marketplace === 'walmart' ? (
                                                    deal?.marketplace_data?.item_id
                                                ) : (
                                                    deal.marketplace === 'ebay' ? (
                                                        deal?.marketplace_data?.upc
                                                    ) : ''
                                                )
                                            );
                                            if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                                window.chrome.runtime.sendMessage(EXT_ID, {
                                                    type: 'MSP_SEARCH_INTENT',
                                                    marketplace: deal.marketplace,
                                                    identifier: EXTidentifier,
                                                    title: deal.title,
                                                    deal,
                                                });
                                            }
                                            if (!title && upc) {
                                                window.open(`https://www.google.com/search?q=${encodeURIComponent(upc)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                            }
                                            if (title) {
                                                window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                            }
                                        }}
                                        src={deal.img || '/checking.gif'}
                                        alt="Source"
                                        className="w-16 h-16 cursor-pointer object-contain rounded border-2 border-red-500 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl relative bg-white opacity-50"
                                        onError={(e) => e.target.src = '/checking.gif'}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold border-2 border-white shadow-lg text-sm">
                                            ✕
                                        </div>
                                    </div>
                                </div>
                            ) : deal.reverse_scan_status === "no_match" ? (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const upc = deal.marketplace_data?.upc || deal.marketplace_data?.ean || deal.marketplace_data?.gtin || deal.upc || '';
                                        const title = deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                        const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                        const EXTidentifier = deal.marketplace === 'amazon' ? (
                                            deal?.marketplace_data?.asin
                                        ) : (
                                            deal.marketplace === 'walmart' ? (
                                                deal?.marketplace_data?.item_id
                                            ) : (
                                                deal.marketplace === 'ebay' ? (
                                                    deal?.marketplace_data?.upc
                                                ) : ''
                                            )
                                        );
                                        if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                            window.chrome.runtime.sendMessage(EXT_ID, {
                                                type: 'MSP_SEARCH_INTENT',
                                                marketplace: deal.marketplace,
                                                identifier: EXTidentifier,
                                                title: deal.title,
                                                deal,
                                            });
                                        }
                                        if (!title && upc) {
                                            window.open(`https://www.google.com/search?q=${encodeURIComponent(upc)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                        }
                                        if (title) {
                                            window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                        }
                                    }}
                                    className="relative group cursor-pointer"
                                >
                                    <img
                                        src={deal.img || '/checking.gif'}
                                        alt="Source"
                                        className="w-16 h-16 object-contain rounded border-2 border-orange-400 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-orange-500 relative bg-white opacity-50"
                                        onError={(e) => e.target.src = '/checking.gif'}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-orange-500 text-white px-1.5 py-0.5 rounded font-bold border-2 border-white shadow-lg text-[9px]">
                                            NO MATCH
                                        </div>
                                    </div>
                                </button>
                            ) : deal.reverse_scan_status === 'stopped' ? (
                                <button
                                    onClick={handleRevealClick}
                                    className="relative group cursor-pointer"
                                    disabled={triggeringReverseScan}
                                    title="Search was paused - click to reveal again"
                                >
                                    <img
                                        src={deal.img || '/reveal.png'}
                                        alt="Source"
                                        className="w-16 h-16 object-contain rounded border-2 border-gray-400 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-gray-500 relative bg-white opacity-60"
                                        onError={(e) => e.target.src = '/reveal.png'}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-gray-500 text-white px-1.5 py-0.5 rounded font-bold border-2 border-white shadow-lg text-[9px]">
                                            ⏸ PAUSED
                                        </div>
                                    </div>
                                </button>
                            ) : supplierLogo === 'checking.gif' ? (
                                <button
                                    onClick={() => setShowSearchingPopup(true)}
                                    className="relative group cursor-pointer"
                                    title="Click to use Universal Reverse Scan while waiting"
                                >
                                    <img
                                        src={deal.img || '/checking.gif'}
                                        alt="Source"
                                        className="w-16 h-16 object-contain rounded border-2 border-blue-300 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-blue-400 relative bg-white"
                                        onError={(e) => e.target.src = '/checking.gif'}
                                    />
                                </button>
                            ) : (
                                <div
                                    className="relative group cursor-pointer"
                                    onClick={(e) => {
                                        if (hasMultipleSources) {
                                            e.preventDefault();
                                            setShowMultiSourcePopup(true);
                                        } else {
                                            if (openUrl) {
                                                window.open(openUrl, '_blank');
                                            }
                                        }
                                    }}
                                >
                                    <img
                                        src={deal.img || supplierLogo || '/checking.gif'}
                                        alt="Source"
                                        className="w-16 h-16 object-contain rounded border-2 border-gray-200 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-yellow-400 relative bg-white"
                                        onError={(e) => e.target.src = supplierLogo || '/noimage.png'}
                                    />
                                    {hasMultipleSources && (
                                        <div className="absolute -top-1 -right-1 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold border-2 border-white shadow-lg">
                                            {totalSources}
                                        </div>
                                    )}
                                </div>
                            )}

                            {deal.reverse_scan_status === "failed" ? (
                                <div className="relative group flex-shrink-0 flex items-center gap-1">
                                    <span className="text-[10px] font-bold text-red-600">FAILED</span>
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-red-500 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        Could not find suppliers
                                        <div className="absolute top-full left-1/2 -translate-x-1/2">
                                            <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-red-500"></div>
                                        </div>
                                    </div>
                                </div>
                            ) : deal.reverse_scan_status === "no_match" ? (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const upc = deal.marketplace_data?.upc || deal.marketplace_data?.ean || deal.marketplace_data?.gtin || deal.upc || '';
                                        const title = deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                        const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                        const EXTidentifier = deal.marketplace === 'amazon' ? (
                                            deal?.marketplace_data?.asin
                                        ) : (
                                            deal.marketplace === 'walmart' ? (
                                                deal?.marketplace_data?.item_id
                                            ) : (
                                                deal.marketplace === 'ebay' ? (
                                                    deal?.marketplace_data?.upc
                                                ) : ''
                                            )
                                        );
                                        if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                            window.chrome.runtime.sendMessage(EXT_ID, {
                                                type: 'MSP_SEARCH_INTENT',
                                                marketplace: deal.marketplace,
                                                identifier: EXTidentifier,
                                                title: deal.title,
                                                deal,
                                            });
                                        }
                                        if (!title && upc) {
                                            window.open(`https://www.google.com/search?q=${encodeURIComponent(upc)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                        }
                                        if (title) {
                                            window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                        }
                                    }}
                                    className="relative group flex-shrink-0 flex items-center gap-1 hover:bg-orange-100 rounded transition-all cursor-pointer px-1"
                                >
                                    <span className="text-[10px] font-bold text-orange-600">SEARCH</span>
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-orange-500 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        No suppliers found - search manually
                                        <div className="absolute top-full left-1/2 -translate-x-1/2">
                                            <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-orange-500"></div>
                                        </div>
                                    </div>
                                </button>
                            ) : supplierLogo !== "checking.gif" ? (
                                <div
                                    className="relative group flex-shrink-0 flex items-center gap-1 cursor-pointer"
                                    onClick={() => {
                                        if (hasMultipleSources) {
                                            setShowMultiSourcePopup(true);
                                        } else if (openUrl) {
                                            window.open(openUrl, '_blank');
                                        }
                                    }}
                                >
                                    <span className="text-[10px] font-semibold text-gray-600">BUY</span>
                                    <img
                                        src={supplierLogo}
                                        alt={deal.supplier_name}
                                        className="w-4 h-4 object-contain"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        {hasMultipleSources
                                            ? `${totalSources} suppliers available - click to choose`
                                            : `Buy from ${deal.supplier_name ? deal.supplier_name.charAt(0).toUpperCase() + deal.supplier_name.slice(1) : "N/A"}`
                                        }
                                        <div className="absolute top-full left-1/2 -translate-x-1/2">
                                            <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-gray-800"></div>
                                        </div>
                                    </div>
                                </div>
                            ) : deal.reverse_scan_status === "not_scanned" ? (
                                <button
                                    onClick={handleRevealClick}
                                    disabled={triggeringReverseScan}
                                    className="relative group flex-shrink-0 flex items-center gap-1 hover:bg-yellow-200 rounded transition-all cursor-pointer"
                                    title={deal.upc && String(deal.upc).trim().match(/^\d+$/) ? "Click to find supplier" : "No UPC data - click to search manually on Google"}
                                >
                                    <span className="text-[10px] font-bold text-yellow-800">SUPPLIER</span>
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        Click to find where to buy it!
                                        <div className="absolute top-full left-1/2 -translate-x-1/2">
                                            <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-gray-800"></div>
                                        </div>
                                    </div>
                                </button>
                            ) : deal.reverse_scan_status === 'stopped' ? (
                                <button
                                    onClick={handleRevealClick}
                                    disabled={triggeringReverseScan}
                                    className="relative group flex-shrink-0 flex items-center gap-1 hover:bg-gray-200 rounded transition-all cursor-pointer px-1"
                                    title="Paused - click to reveal supplier again"
                                >
                                    <span className="text-[10px] font-bold text-gray-500">⏸ PAUSED</span>
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowSearchingPopup(true)}
                                    className="relative group flex-shrink-0 flex items-center gap-1 hover:bg-blue-100 rounded transition-all cursor-pointer px-1"
                                    title="Click to use Universal Reverse Scan while waiting"
                                >
                                    <RefreshCw className="animate-spin [animation-duration:2s] w-3 h-3 object-contain flex-shrink-0 text-blue-600" />
                                    <span className="text-[10px] font-semibold text-blue-600">SEARCHING</span>
                                </button>
                            )}
                        </div>

                        {/* Sell On Image */}
                        <div className="flex flex-col items-center gap-1">
                            <a href={
                                deal.marketplace === 'amazon' && data.asin ? `https://www.amazon${amazonDomain}/dp/${data.asin}` :
                                    deal.marketplace === 'walmart' && data.item_id ? `https://www.walmart.com/ip/${data.item_id}` :
                                        deal.marketplace === 'ebay' && data.ebay_search_link ? data.ebay_search_link : '#'
                            } target="_blank" rel="noopener noreferrer" className="relative group cursor-pointer">
                                <img
                                    src={(deal.marketplace === 'amazon' ? data.image : deal.marketplace === 'walmart' ? data.image : deal.marketplace === 'ebay' ? data.image : null)}
                                    alt="Marketplace"
                                    className="w-16 h-16 object-contain rounded border-2 border-gray-200 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-green-400 relative bg-white"
                                    onError={(e) => e.target.src = '/noimage.png'}
                                />
                            </a>
                            {marketplaceLogo && (
                                <div className="relative group flex-shrink-0 flex items-center gap-1">
                                    <span className="text-[10px] font-semibold text-gray-600">SELL</span>
                                    <img
                                        src={marketplaceLogo}
                                        alt={deal.marketplace}
                                        className="w-4 h-4 object-contain"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                        Sell on {String(deal.marketplace).charAt(0).toUpperCase() + String(deal.marketplace).slice(1)}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2">
                                            <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-gray-800"></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {quickLinks.length > 0 && openUrl && (
                            <button
                                onClick={() => hasMultipleSources ? setShowMultiSourcePopup(true) : quickLinks[0].action()}
                                className="absolute bottom-0 left-full ml-4 mt-1 text-xs text-teal-600 hover:text-teal-800 font-semibold flex items-center gap-1 whitespace-nowrap"
                            >
                                {hasMultipleSources ? '🛒' : '🔗'} {hasMultipleSources ? `Choose from ${totalSources} Suppliers` : 'Open Both'}
                            </button>
                        )}
                    </div>

                    {/* Shared Data - 6 Boxes */}
                    <div className={`grid gap-2 grid-cols-3 sm:grid-cols-4 ${deal.marketplace === 'amazon' && data.asin ? 'md:grid-cols-7' : deal.marketplace === 'walmart' ? 'md:grid-cols-7' : 'md:grid-cols-6'}`}>
                        {/* Cost Price */}
                        <div className={`rounded p-2 border ${costPriceColor}`}>
                            <div className="text-gray-600 text-[10px] font-semibold text-center flex items-center justify-center gap-0.5 pb-0.5">
                                Cost Price
                                <PackSizeButton packSize={packSize} onPackSizeChange={setPackSize} />
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowCashbackModal(true);
                                    }}
                                    className="text-orange-600 hover:text-orange-800 transition flex items-center"
                                    title="Find cashback & discounted gift cards"
                                >
                                    <span className="text-xs">💸</span>
                                </button>
                                {couponAmount > 0 && <span className="text-green-600">(-{couponAmount}%)</span>}
                            </div>
                            <div className="font-bold text-sm text-gray-800 text-center flex items-center justify-center gap-1 pt-[1px]">
                                {deal.reverse_scan_status === "failed" ? (
                                    <div className="relative group flex-shrink-0 flex items-center gap-1">
                                        <span className="text-[10px] font-bold text-red-600">FAILED</span>
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-red-500 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                            Could not find suppliers
                                            <div className="absolute top-full left-1/2 -translate-x-1/2">
                                                <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-red-500"></div>
                                            </div>
                                        </div>
                                    </div>
                                ) : deal.reverse_scan_status === "no_match" ? (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const upc = deal.marketplace_data?.upc || deal.marketplace_data?.ean || deal.marketplace_data?.gtin || deal.upc || '';
                                            const title = deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                            const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                            const EXTidentifier = deal.marketplace === 'amazon' ? (
                                                deal?.marketplace_data?.asin
                                            ) : (
                                                deal.marketplace === 'walmart' ? (
                                                    deal?.marketplace_data?.item_id
                                                ) : (
                                                    deal.marketplace === 'ebay' ? (
                                                        deal?.marketplace_data?.upc
                                                    ) : ''
                                                )
                                            );
                                            if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                                window.chrome.runtime.sendMessage(EXT_ID, {
                                                    type: 'MSP_SEARCH_INTENT',
                                                    marketplace: deal.marketplace,
                                                    identifier: EXTidentifier,
                                                    title: deal.title,
                                                    deal,
                                                });
                                            }
                                            if (!title && upc) {
                                                window.open(`https://www.google.com/search?q=${encodeURIComponent(upc)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                            }
                                            if (title) {
                                                window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                            }
                                        }}
                                        className="relative group flex-shrink-0 flex items-center gap-1 hover:bg-orange-100 rounded transition-all cursor-pointer px-1"
                                    >
                                        <span className="text-[10px] font-bold text-orange-600">SEARCH</span>
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-orange-500 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                            No suppliers found - search manually
                                            <div className="absolute top-full left-1/2 -translate-x-1/2">
                                                <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-orange-500"></div>
                                            </div>
                                        </div>
                                    </button>
                                ) : supplierLogo !== "checking.gif" ? (
                                    <>
                                        ${((deal.source_price || 0) * (1 - (couponAmount / 100)) * packSize).toFixed(2)}
                                        {!hasMultipleSources && <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const overrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');

                                                // Get original price (stored or current if never edited)
                                                const originalPrice = overrides[deal.id]?.original_source_price ?? deal.source_price;
                                                const currentPrice = deal.source_price;

                                                const promptText = originalPrice?.toFixed(2) !== currentPrice?.toFixed(2)
                                                    ? `Enter new cost price (originally $${originalPrice?.toFixed(2)}):`
                                                    : 'Enter new cost price:';

                                                const newPrice = prompt(promptText, currentPrice?.toFixed(2));
                                                if (newPrice !== null && !isNaN(parseFloat(newPrice))) {
                                                    // Save original on first edit only
                                                    if (!overrides[deal.id]) {
                                                        overrides[deal.id] = { original_source_price: deal.source_price };
                                                    }

                                                    // Update the deal object
                                                    deal.source_price = parseFloat(newPrice);

                                                    // Save the new price
                                                    overrides[deal.id].source_price = parseFloat(newPrice);
                                                    localStorage.setItem('deal_price_overrides', JSON.stringify(overrides));

                                                    if (onSettingsChange) onSettingsChange();
                                                }
                                            }}
                                            className="text-gray-500 hover:text-gray-700 cursor-pointer text-[12px]"
                                            title="Edit cost price"
                                        >
                                            ✏️
                                        </button>}
                                        {hasMultipleSources && <span className="text-blue-600 text-[9px]">(Lowest)</span>}
                                    </>
                                ) : deal.reverse_scan_status === "not_scanned" ? (
                                    <>
                                        <button
                                            onClick={handleRevealClick}
                                            disabled={triggeringReverseScan}
                                            className="text-yellow-700 hover:text-yellow-900 cursor-pointer text-[13px] flex-shrink-0"
                                            title={deal.upc && String(deal.upc).trim().match(/^\d+$/) ? "Click to find supplier" : "No UPC data - click to search manually on Google"}
                                        >
                                            🔍
                                        </button>
                                        <span className="text-gray-400 text-[11px]">$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            inputMode="decimal"
                                            value={manualCostInput}
                                            placeholder="0.00"
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                setManualCostInput(raw);
                                                const val = raw === '' ? 0 : parseFloat(raw);
                                                if (isNaN(val)) return;
                                                saveManualCost(val);
                                            }}
                                            className="w-11 text-center border border-gray-300 rounded px-0.5 py-0.5 text-[11px] font-bold focus:outline-none focus:border-teal-400"
                                        />
                                    </>
                                ) : deal.reverse_scan_status === 'stopped' ? (
                                    <>
                                        <button
                                            onClick={handleRevealClick}
                                            disabled={triggeringReverseScan}
                                            className="text-gray-500 hover:text-gray-700 cursor-pointer text-[13px] flex-shrink-0"
                                            title="Paused - click to reveal supplier again"
                                        >
                                            ⏸
                                        </button>
                                        <span className="text-gray-400 text-[11px]">$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            inputMode="decimal"
                                            value={manualCostInput}
                                            placeholder="0.00"
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                setManualCostInput(raw);
                                                const val = raw === '' ? 0 : parseFloat(raw);
                                                if (isNaN(val)) return;
                                                saveManualCost(val);
                                            }}
                                            className="w-11 text-center border border-gray-300 rounded px-0.5 py-0.5 text-[11px] font-bold focus:outline-none focus:border-teal-400"
                                        />
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => setShowSearchingPopup(true)}
                                            className="text-blue-600 hover:text-blue-800 cursor-pointer flex-shrink-0"
                                            title="Click to use Universal Reverse Scan while waiting"
                                        >
                                            <RefreshCw className="animate-spin [animation-duration:2s] w-3 h-3" />
                                        </button>
                                        <span className="text-gray-400 text-[11px]">$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            inputMode="decimal"
                                            value={manualCostInput}
                                            placeholder="0.00"
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                setManualCostInput(raw);
                                                const val = raw === '' ? 0 : parseFloat(raw);
                                                if (isNaN(val)) return;
                                                saveManualCost(val);
                                            }}
                                            className="w-11 text-center border border-gray-300 rounded px-0.5 py-0.5 text-[11px] font-bold focus:outline-none focus:border-teal-400"
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Sell Price */}
                        <div className="bg-green-50 rounded p-2 border border-green-200 relative group">
                            <div className="text-gray-600 text-[10px] font-semibold text-center mb-1">Sell Price{window.location.pathname.includes('a2a') ? " (90d avg)" : ""}{deal.storefront_price && (
                                <span className="text-[10px] text-center ml-1">
                                    <span className="text-gray-500"
                                        style={{ color: Math.abs(deal.storefront_price - metrics.sellPrice) < 0.01 ? 'green' : 'red' }}>
                                        (Seller ${deal.storefront_price.toFixed(2)})
                                    </span>
                                </span>
                            )}</div>
                            <div className="font-bold text-sm text-green-800 text-center flex items-center justify-center gap-1">
                                {currency}{metrics.sellPrice?.toFixed(2)}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const data = deal.marketplace_data || {};
                                        const overrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');

                                        // Get current sell price from data
                                        const currentSellPrice = (deal.marketplace === 'amazon' ? data.az_price :
                                            deal.marketplace === 'walmart' ? data.price_value :
                                                deal.marketplace === 'ebay' ? data.lowest_price : 0);

                                        // Get original price (stored or current if never edited)
                                        const originalSellPrice = overrides[deal.id]?.original_sell_price ?? currentSellPrice;

                                        const promptText = originalSellPrice?.toFixed(2) !== currentSellPrice?.toFixed(2)
                                            ? `Enter new sell price (originally $${originalSellPrice?.toFixed(2)}):`
                                            : 'Enter new sell price:';

                                        const newPrice = prompt(promptText, currentSellPrice?.toFixed(2));
                                        if (newPrice !== null && !isNaN(parseFloat(newPrice))) {
                                            const parsedPrice = parseFloat(newPrice);

                                            // Save original on first edit only
                                            if (!overrides[deal.id]) {
                                                overrides[deal.id] = { original_sell_price: currentSellPrice };
                                            }

                                            // Update the deal object
                                            if (deal.marketplace === 'amazon') {
                                                data.az_price = parsedPrice;
                                            } else if (deal.marketplace === 'walmart') {
                                                data.price_value = parsedPrice;
                                            } else if (deal.marketplace === 'ebay') {
                                                data.lowest_price = parsedPrice;
                                            }

                                            // Save the new price
                                            overrides[deal.id].sell_price = parsedPrice;
                                            localStorage.setItem('deal_price_overrides', JSON.stringify(overrides));

                                            if (onSettingsChange) onSettingsChange();
                                        }
                                    }}
                                    className="text-gray-500 hover:text-gray-700 cursor-pointer text-[12px]"
                                    title="Edit sell price"
                                >
                                    ✏️
                                </button>
                            </div>

                            {deal.marketplace === 'amazon' && (
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-white border-2 border-green-300 rounded-lg shadow-xl p-3 min-w-[min(140px,70vw)]">
                                    <div className="text-xs font-semibold text-gray-700 mb-2 text-center border-b pb-1">Average Price</div>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs text-gray-600">30d:</span>
                                            <span className="text-sm font-bold text-green-700">${(data.az_price_avg30 || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs text-gray-600">90d:</span>
                                            <span className="text-sm font-bold text-green-700">${(data.az_price_avg90 || 0).toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                                        <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-green-300"></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Profit */}
                        <div className={`rounded p-2 border ${profit > 0 ? 'bg-green-100 border-green-300' : 'bg-gray-100 border-gray-300'}`}>
                            <div className="text-gray-600 text-[10px] font-semibold text-center mb-1">Profit</div>
                            <div className={`font-bold text-sm text-center ${profit > 0 ? 'text-green-900' : 'text-gray-700'}`}>
                                ${profit?.toFixed(2)}
                            </div>
                        </div>

                        {/* ROI */}
                        <div className={`rounded p-2 border ${metrics.roi > 0 ? 'bg-purple-100 border-purple-300' : 'bg-gray-100 border-gray-300'}`}>
                            <div className="text-gray-600 text-[10px] font-semibold text-center mb-1">ROI</div>
                            <div className={`font-bold text-sm text-center ${metrics.roi > 0 ? 'text-purple-900' : 'text-gray-700'}`}>
                                {metrics.roi?.toFixed(1)}%
                            </div>
                        </div>

                        {/* Total Fees */}
                        <div className="bg-red-50 rounded p-2 border border-red-200 relative group">
                            <div className="text-gray-600 text-[10px] font-semibold text-center flex items-center justify-center gap-1">
                                {deal.marketplace === 'amazon' && onFulfillmentChange && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onFulfillmentChange(fulfillmentType === 'fba' ? 'fbm' : 'fba'); }}
                                        className={`-ml-8 px-1 rounded text-[9px] font-bold border transition-colors ${fulfillmentType === 'fba' ? 'bg-orange-100 border-orange-400 text-orange-700' : 'bg-gray-100 border-gray-400 text-gray-600'}`}
                                    >
                                        {fulfillmentType === 'fba' ? 'FBA' : 'FBM'}
                                    </button>
                                )}
                                {deal.marketplace === 'walmart' && onFulfillmentChange && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onFulfillmentChange(fulfillmentType === 'wfs' ? 'sf' : 'wfs'); }}
                                        className={`-ml-8 px-1 rounded text-[9px] font-bold border transition-colors ${fulfillmentType === 'wfs' ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-gray-100 border-gray-400 text-gray-600'}`}
                                    >
                                        {fulfillmentType === 'wfs' ? 'WFS' : 'SF'}
                                    </button>
                                )}
                                Total Fees
                                {deal.marketplace === 'walmart' && metrics.missingData && (metrics.missingData.category || (fulfillmentType === 'wfs' && (metrics.missingData.weight || metrics.missingData.dimensions))) && (
                                    <span className="text-orange-600 text-s -mt-0.5" title="Missing data - hover to see details">⚠️</span>
                                )}
                            </div>
                            <div className="font-bold text-sm text-red-800 text-center pt-1 flex items-center justify-center gap-1">
                                ${metrics.totalFees?.toFixed(2)}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowFeeSettings(true);
                                    }}
                                    className="text-gray-500 hover:text-gray-700 cursor-pointer text-xs"
                                    title="Configure fee settings"
                                    style={{ border: '1px solid rgba(0,0,0,0.15)', borderRadius: "20px" }}
                                >
                                    ⚙️
                                </button>
                            </div>

                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-white border-2 border-red-300 rounded-lg shadow-xl p-3 min-w-[min(180px,80vw)]">
                                <div className="text-xs font-semibold text-gray-700 mb-2 text-center border-b pb-1">Fee Breakdown</div>
                                {deal.marketplace === 'walmart' && metrics.missingData && (metrics.missingData.category || (fulfillmentType === 'wfs' && (metrics.missingData.weight || metrics.missingData.dimensions))) && (
                                    <div className="mb-2 pb-2 border-b border-orange-200">
                                        {fulfillmentType === 'wfs' && metrics.missingData.weight && (
                                            <div className="flex items-center gap-1 text-orange-600 text-xs mb-1">
                                                <span>⚠️</span>
                                                <span>Weight missing (WFS)</span>
                                            </div>
                                        )}
                                        {fulfillmentType === 'wfs' && metrics.missingData.dimensions && (
                                            <div className="flex items-center gap-1 text-orange-600 text-xs mb-1">
                                                <span>⚠️</span>
                                                <span>Dimensions missing (WFS)</span>
                                            </div>
                                        )}
                                        {metrics.missingData.category && (
                                            <div className="flex items-center gap-1 text-orange-600 text-xs mb-1">
                                                <span>⚠️</span>
                                                <span>Category missing (Referral)</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="space-y-1.5 text-xs">
                                    {deal.marketplace === 'amazon' && (
                                        <>
                                            {fulfillmentType === 'fba' && metrics.breakdown.fbaFee > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">FBA:</span>
                                                    <span className="font-semibold">${metrics.breakdown.fbaFee?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Referral:</span>
                                                <span className="font-semibold">${metrics.breakdown.refFee?.toFixed(2)}</span>
                                            </div>
                                            {metrics.breakdown.placementFees > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Placement:</span>
                                                    <span className="font-semibold">${metrics.breakdown.placementFees?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {metrics.breakdown.inboundShipping > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Inbound:</span>
                                                    <span className="font-semibold">${metrics.breakdown.inboundShipping?.toFixed(2)}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {deal.marketplace === 'walmart' && (
                                        <>
                                            {fulfillmentType === 'wfs' && metrics.breakdown.wfsFee > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">WFS{(metrics.missingData.weight || metrics.missingData.dimensions) && " ($3.45 Default)"}:</span>
                                                    <span className="font-semibold">${metrics.breakdown.wfsFee?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Referral:</span>
                                                <span className="font-semibold">${metrics.breakdown.refFee?.toFixed(2)}</span>
                                            </div>
                                            {metrics.breakdown.inboundShipping > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Inbound:</span>
                                                    <span className="font-semibold">${metrics.breakdown.inboundShipping?.toFixed(2)}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {deal.marketplace === 'ebay' && (
                                        <>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Final Value Fee (Most Ctg. 13.6%):</span>
                                                <span className="font-semibold">${metrics.breakdown.finalValueFee?.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Per-Order Fee:</span>
                                                <span className="font-semibold">${metrics.breakdown.perOrderFee?.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Payment Processing (2.35% + $0.30):</span>
                                                <span className="font-semibold">${metrics.breakdown.paymentProcessingFee?.toFixed(2)}</span>
                                            </div>
                                            {metrics.breakdown.shippingCharge > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Shipping Charge (Revenue):</span>
                                                    <span className="font-semibold text-green-600">+${metrics.breakdown.shippingCharge?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {metrics.breakdown.shippingCost > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Shipping Cost (Expense):</span>
                                                    <span className="font-semibold">${metrics.breakdown.shippingCost?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {metrics.breakdown.salesTax > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Sales Tax:</span>
                                                    <span className="font-semibold">${metrics.breakdown.salesTax?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {metrics.breakdown.packagingCost > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Packaging:</span>
                                                    <span className="font-semibold">${metrics.breakdown.packagingCost?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {metrics.breakdown.otherCosts > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Other:</span>
                                                    <span className="font-semibold">${metrics.breakdown.otherCosts?.toFixed(2)}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {metrics.breakdown.salesTax > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Sales Tax:</span>
                                            <span className="font-semibold">${metrics.breakdown.salesTax?.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {metrics.breakdown.otherCosts > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Other:</span>
                                            <span className="font-semibold">${metrics.breakdown.otherCosts?.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {metrics.breakdown.fulfillmentCost > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Fulfillment Cost:</span>
                                            <span className="font-semibold">${metrics.breakdown.fulfillmentCost?.toFixed(2)}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                                    <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-red-300"></div>
                                </div>
                            </div>
                        </div>

                        {deal.marketplace !== "ebay" && (
                            <div className={`relative group rounded p-2 border flex flex-col items-center justify-center ${deal.marketplace === 'amazon' && !amazonLinked
                                ? 'bg-yellow-50 border-yellow-300'
                                : deal.gated_status === 'gated' ? 'bg-red-50 border-red-400'
                                    : deal.gated_status === 'approval' ? 'bg-orange-50 border-orange-400'
                                        : deal.gated_status === 'ungated' ? 'bg-green-50 border-green-400'
                                            : 'bg-gray-50 border-gray-200'
                                }`}>
                                {deal.marketplace === 'walmart' ? (
                                    <>
                                        <div className="text-gray-600 text-[10px] font-semibold text-center mb-1 flex items-center justify-center gap-1">
                                            WFS Fee
                                            {(() => {
                                                const w = data.weight || deal.weight;
                                                const d = data.dimensions;
                                                const hasDims = d && d.length && d.width && d.height;
                                                return (
                                                    <div className="relative group/dims cursor-help">
                                                        <span className="text-[9px]">ℹ️</span>
                                                        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover/dims:block z-50 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                                            {w ? `Weight: ${w}lb` : 'Weight: unknown'}<br />
                                                            {hasDims ? `${d.length}" × ${d.width}" × ${d.height}"` : 'Dimensions: unknown'}
                                                            <div className="absolute top-full left-1/2 -translate-x-1/2">
                                                                <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-gray-800"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <div className="font-bold text-sm text-center text-blue-800 flex items-center justify-center gap-1">
                                            {(() => {
                                                const w = data.weight || deal.weight;
                                                const d = data.dimensions;
                                                const hasDims = d && d.length && d.width && d.height;
                                                if (w && hasDims) {
                                                    const fee = calculateWFSFee(d, w, {
                                                        isApparel: data.category?.toLowerCase().includes("clothing") || data.category?.toLowerCase().includes("apparel"),
                                                        retailPrice: data.price_value || 0
                                                    });
                                                    return `$${fee.toFixed(2)}`;
                                                }
                                                return <span className="text-[10px] text-gray-400">No dims</span>;
                                            })()}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setShowWfsDimsModal(true); }}
                                                className="text-gray-400 hover:text-gray-600 text-[11px]"
                                                title="Edit weight"
                                            >✏️</button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-gray-600 text-[10px] font-semibold text-center mb-1">Gated</div>
                                        <div className="flex items-center justify-center gap-1">
                                            {deal.marketplace === 'amazon' && !amazonLinked ? (
                                                <button
                                                    onClick={async () => {
                                                        window.open(
                                                            `https://sellercentral.amazon${authService.getAmazonMarketplace()}/apps/authorize/consent?application_id=amzn1.sp.solution.18aecca2-d85f-492c-b0ba-d121066f7ef8&state=${encodeURIComponent(authService.credentials?.email || '')}`,
                                                            "_blank"
                                                        );
                                                    }}
                                                    style={{ margin: "auto", border: "none", background: "transparent", cursor: "pointer" }}
                                                >
                                                    <img
                                                        src="https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_101x46.png"
                                                        style={{ height: 25 }}
                                                        alt="Link Amazon Account"
                                                    />
                                                </button>
                                            ) : (
                                                <>
                                                    {onRefreshGated && (
                                                        <button
                                                            onClick={onRefreshGated}
                                                            disabled={refreshing}
                                                            className="text-blue-600 hover:text-blue-800 disabled:opacity-50 transition"
                                                            title="Refresh gated status"
                                                        >
                                                            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                                                        </button>
                                                    )}
                                                    {getGatedBadge(deal.gated_status, data.asin)}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Walmart: Badges & Reviews box */}
                        {deal.marketplace === 'walmart' && (
                            <div className="rounded p-2 border bg-gray-50 border-gray-200 flex flex-col items-center justify-center">
                                {data.badges && data.badges.length > 0 ? (
                                    <>
                                        <div className="text-gray-600 text-[10px] font-semibold text-center mb-1">Badge</div>
                                        <div
                                            className="text-[10px] font-bold text-center px-1.5 py-0.5 rounded truncate w-full"
                                            style={{ backgroundColor: `hsl(${(data.badges[0].id ?? 0) * 47 % 360}, 70%, 85%)`, color: '#1a1a1a', border: `1px solid hsl(${(data.badges[0].id ?? 0) * 47 % 360}, 60%, 60%)` }}
                                            title={data.badges[0].text}
                                        >
                                            {data.badges[0].text}
                                        </div>
                                        {data.badges.length > 1 && (
                                            <div className="text-[9px] text-gray-400 text-center">+{data.badges.length - 1} more</div>
                                        )}
                                    </>
                                ) : data.number_of_reviews != null ? (
                                    <>
                                        <div className="text-gray-600 text-[10px] font-semibold text-center mb-1">Reviews</div>
                                        <div className="font-semibold text-sm text-center text-gray-800">
                                            {data.number_of_reviews.toLocaleString()}{data.average_rating != null ? ` (★${data.average_rating.toFixed(1)})` : ''}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-gray-600 text-[10px] font-semibold text-center mb-1">Reviews</div>
                                        <div className="text-[10px] text-gray-400 text-center">N/A</div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* eBay: Offers box */}
                        {deal.marketplace === 'ebay' && (
                            <div className="rounded p-2 border bg-gray-50 border-gray-200 flex flex-col items-center justify-center">
                                <div className="text-gray-600 text-[10px] font-semibold text-center mb-1">Offers</div>
                                <div className="font-bold text-sm text-center text-gray-800">
                                    {data.total_offers != null && data.total_offers !== -1 ? data.total_offers : 'N/A'}
                                </div>
                            </div>
                        )}

                        {deal.marketplace === 'amazon' && data.asin && (() => {
                            const keepaLink = quickLinks.find(l => l.label === 'Keepa');
                            if (!keepaLink) return null;
                            const savedSettings = JSON.parse(localStorage.getItem('keepa_chart_settings') || '{"range":90,"salesrank":true,"amazon":true,"new":true,"bb":true,"fba":true}');
                            const cacheKey = `${data.asin}_${savedSettings.range}_${savedSettings.salesrank}_${savedSettings.amazon}_${savedSettings.new}_${savedSettings.bb}_${savedSettings.fba}`;
                            const cachedUrl = keepaChartCache[cacheKey];
                            return (
                                <div
                                    className="relative group/keepa rounded border border-gray-200 bg-gray-50 overflow-visible cursor-pointer hover:border-teal-400 transition-colors h-full"
                                    onClick={() => window.open(keepaLink.url, '_blank')}
                                    onMouseEnter={() => fetchKeepaChart()}
                                >
                                    {/* Mini chart filling the whole box */}
                                    <img
                                        src={`https://wsrv.nl/?url=${encodeURIComponent(`https://graph.keepa.com/pricehistory.png?asin=${data.asin}&domain=${{ '.com': 'com', '.co.uk': 'co.uk', '.ca': 'ca', '.com.mx': 'com.mx' }[amazonDomain] || 'com'}`)}`}
                                        alt="Keepa"
                                        className="w-full h-full object-contain rounded"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    {/* Fallback */}
                                    <div className="w-full h-full justify-center items-center gap-1 text-gray-500 text-xs" style={{ display: 'none' }}>
                                        <img src="icons/keepa.png" className="w-4 h-4" alt="Keepa" />
                                        <span>Keepa</span>
                                    </div>

                                    {/* Hover popup */}
                                    <div
                                        className="absolute right-0 bottom-full mb-2 hidden group-hover/keepa:block z-[60]"
                                        style={{ minWidth: 'min(500px, 90vw)' }}
                                        onClick={(e) => { e.stopPropagation(); setShowKeepaSettings(true); }}
                                    >
                                        <div className="bg-white border-2 border-teal-300 rounded-lg shadow-2xl p-3">
                                            <div className="flex items-center justify-between mb-2 border-b pb-1">
                                                <span className="text-xs font-semibold text-gray-700">
                                                    Keepa Price History ({savedSettings.range} days)
                                                </span>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setShowKeepaSettings(true); }}
                                                    className="text-gray-400 hover:text-gray-700 text-sm"
                                                    title="Configure chart"
                                                >
                                                    ⚙️
                                                </button>
                                            </div>
                                            {keepaChartLoading ? (
                                                <div className="flex items-center justify-center py-8">
                                                    <RefreshCw className="animate-spin text-teal-600" size={24} />
                                                    <span className="ml-2 text-sm text-gray-600">Loading chart...</span>
                                                </div>
                                            ) : cachedUrl === 'ERROR' ? (
                                                <div className="text-center py-4 text-sm text-red-600">Failed to load chart.</div>
                                            ) : cachedUrl ? (
                                                <img
                                                    src={cachedUrl}
                                                    alt="Keepa Chart"
                                                    style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            ) : (
                                                <div className="text-center py-4 text-sm text-gray-500">Hover to load chart</div>
                                            )}
                                            <div className="absolute -bottom-2 right-8">
                                                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-teal-300"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* View Details Button */}
                    <div className="flex flex-col gap-1 items-center">
                        <button
                            onClick={() => setShowFullCard(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-semibold text-sm whitespace-nowrap"
                        >
                            Details
                        </button>
                        {deal.marketplace === 'amazon' && data.asin && (
                            <div className="text-[10px] text-center text-black font-bold whitespace-nowrap">
                                {data.keepa_monthly_sold > 0 && (
                                    <span className="text-green-600 mr-0.5" title="Verified by Keepa">✓</span>
                                )}
                                {data.keepa_monthly_sold > 0
                                    ? `${data.keepa_monthly_sold}+`
                                    : (amazonDomain !== '.com' ? Math.round((data.estimate_sales_30_days || 0) / 6) : (data.estimate_sales_30_days || 0))}
                                <span className="text-gray-400"> /mo {data.keepa_monthly_sold > 0 ? "sold" : "est. sales"}</span>
                            </div>
                        )}
                    </div>
                </div >
            </div >

            {/* Full Card Modal */}
            {
                showFullCard && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4"
                        onClick={() => setShowFullCard(false)}
                    >
                        <div
                            className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                                <h3 className="text-lg font-semibold text-gray-800">Deal Details - Unselecting "Compact View" will show this view instantly!</h3>
                                <button
                                    onClick={() => setShowFullCard(false)}
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            <div className="p-3 md:p-6 m-3 md:m-6">
                                <DealCard
                                    deal={deal}
                                    couponAmount={couponAmount}
                                    fulfillmentType={fulfillmentType}
                                    onRefreshGated={onRefreshGated}
                                    refreshing={refreshing}
                                    onSettingsChange={onSettingsChange}
                                    amazonLinked={amazonLinked}
                                />
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showMultiSourcePopup && (
                    <MultiSourcePopup
                        allSources={allSources}
                        couponAmount={couponAmount}
                        fulfillmentType={fulfillmentType}
                        onClose={() => setShowMultiSourcePopup(false)}
                        onRefreshGated={onRefreshGated}
                        refreshing={refreshing}
                        onSettingsChange={onSettingsChange}
                        amazonLinked={amazonLinked}
                    />
                )
            }

            {/* Fee Settings Modal */}
            {
                showFeeSettings && (
                    <FeeSettings
                        marketplace={deal.marketplace}
                        onClose={(newSettings) => {
                            if (newSettings) {
                                setFeeSettings(newSettings);
                                if (onSettingsChange) {
                                    onSettingsChange();
                                }
                            }
                            setShowFeeSettings(false);
                        }}
                    />
                )
            }

            {
                showCashbackModal && (
                    <CashbackModal
                        supplierName={deal.supplier_name}
                        onClose={() => setShowCashbackModal(false)}
                    />
                )
            }

            {
                showPopupBlockedModal && (
                    <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center" onClick={() => setShowPopupBlockedModal(false)}>
                        <div className="bg-white rounded-lg shadow-2xl p-5 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
                            <div className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                                ⚠️ Popup Blocked
                            </div>
                            <p className="text-sm text-gray-600 mb-3">
                                Your browser blocked the second link. To open both links at once:
                            </p>
                            <ol className="text-sm text-gray-700 space-y-2 mb-4 list-decimal list-inside">
                                <li>Click the <strong>blocked popup icon</strong> in Chrome's address bar (right side)</li>
                                <li>Select <strong>"Always allow pop-ups from this site"</strong></li>
                                <li>Click <strong>Done</strong>, then try again</li>
                            </ol>
                            <div className="border-t pt-3 flex items-center justify-between">
                                <a
                                    href={(() => {
                                        const d = deal.marketplace_data || {};
                                        return deal.marketplace === 'amazon' && d.asin
                                            ? `https://www.amazon${amazonDomain}/dp/${d.asin}`
                                            : deal.marketplace === 'walmart' && d.item_id
                                                ? `https://www.walmart.com/ip/${d.item_id}`
                                                : deal.marketplace === 'ebay' && d.ebay_search_link
                                                    ? d.ebay_search_link : '#';
                                    })()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-teal-600 hover:text-teal-800 font-semibold underline"
                                    onClick={() => setShowPopupBlockedModal(false)}
                                >
                                    Open marketplace link manually
                                </a>
                                <button
                                    onClick={() => setShowPopupBlockedModal(false)}
                                    className="text-sm text-gray-500 hover:text-gray-700 font-semibold"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {showSearchingPopup && (
                <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                            <RefreshCw className="text-blue-500 animate-spin" size={28} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Still Looking for a Supplier!</h3>
                        <p className="text-gray-600 text-sm mb-4">
                            We're scanning suppliers in the background. While you wait, you can search manually using the <strong>Universal Reverse Scan</strong> — our Chrome extension that finds suppliers instantly.
                        </p>
                        <div className="rounded-xl overflow-hidden mb-5" style={{ aspectRatio: '16/9' }}>
                            <iframe
                                width="100%"
                                height="100%"
                                src="https://www.youtube.com/embed/sFWdAhKzLuw"
                                title="Universal Reverse Scan Tutorial"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                                style={{ display: 'block', width: '100%', height: '100%', minHeight: 160 }}
                            />
                        </div>
                        <button
                            onClick={() => {
                                setShowSearchingPopup(false);
                                const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                const EXTidentifier = deal.marketplace === 'amazon'
                                    ? deal?.marketplace_data?.asin
                                    : deal.marketplace === 'walmart'
                                        ? deal?.marketplace_data?.item_id
                                        : deal?.marketplace_data?.upc || '';
                                if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                    window.chrome.runtime.sendMessage(EXT_ID, {
                                        type: 'MSP_SEARCH_INTENT',
                                        marketplace: deal.marketplace,
                                        identifier: EXTidentifier,
                                        title: deal.title,
                                        deal,
                                    });
                                }
                                const title = deal.title || deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                if (title) {
                                    window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                }
                            }}
                            className="w-full px-4 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white font-semibold rounded-xl hover:opacity-90 transition mb-3"
                        >
                            🔍 Do Universal Reverse Scan
                        </button>
                        <button
                            onClick={() => setShowSearchingPopup(false)}
                            className="w-full px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50 transition"
                        >
                            Close — I'll wait
                        </button>
                    </div>
                </div>
            )}

            {showWfsDimsModal && (
                <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center">
                    <div className="bg-white rounded-lg shadow-2xl p-5 w-80" onClick={e => e.stopPropagation()}>
                        <div className="text-sm font-bold text-gray-800 mb-4">Edit Weight & Dimensions</div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-semibold text-gray-600 block mb-1">Weight (lbs)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    defaultValue={data.weight || deal.weight || ''}
                                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                                    id="wfs-weight-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-600 block mb-1">Length (inches)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    defaultValue={data.dimensions?.length || ''}
                                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                                    id="wfs-length-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-600 block mb-1">Width (inches)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    defaultValue={data.dimensions?.width || ''}
                                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                                    id="wfs-width-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-600 block mb-1">Height (inches)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    defaultValue={data.dimensions?.height || ''}
                                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-teal-400"
                                    id="wfs-height-input"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => setShowWfsDimsModal(false)}
                                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const w = parseFloat(document.getElementById('wfs-weight-input').value);
                                    const l = parseFloat(document.getElementById('wfs-length-input').value);
                                    const wd = parseFloat(document.getElementById('wfs-width-input').value);
                                    const h = parseFloat(document.getElementById('wfs-height-input').value);
                                    if (!isNaN(w)) data.weight = w;
                                    if (!isNaN(l) && !isNaN(wd) && !isNaN(h)) {
                                        data.dimensions = { length: l, width: wd, height: h };
                                    }
                                    const scrollY = window.scrollY;
                                    if (onSettingsChange) onSettingsChange();
                                    requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'instant' }));
                                    setShowWfsDimsModal(false);
                                }}
                                className="flex-1 px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded font-semibold"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MinimizedDealCard;